import { useEditor, TLShape } from "@tldraw/tldraw";
import { useRef, useEffect } from "react";

const HIDE_SELECTION_DURATION_MS = 500;

/**
 * On any change to a selected shape (e.g. style from sidebar), hide the selection
 * outline for 0.5s then show it again. Uses editor instance state isChangingStyle
 * (same as tldraw's style panel). Resizing is excluded.
 */
export function useHideSelectionOnChange() {
  const editor = useEditor();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleShapeChange = (shapeBefore: TLShape, shapeAfter: TLShape) => {
      try {
        if (editor.getPath() === "select.resizing") return;

        const selectedIds = editor.getSelectedShapeIds();
        if (selectedIds.length === 0) return;
        if (!selectedIds.includes(shapeAfter.id)) return;

        // Ignore our own updates that only toggle isShowIndicator (avoid feedback loop)
        const prevIndicator = (shapeBefore.props as any)?.isShowIndicator;
        const nextIndicator = (shapeAfter.props as any)?.isShowIndicator;
        if (prevIndicator !== nextIndicator) {
          return;
        }

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        editor.updateInstanceState({ isChangingStyle: true });

        timeoutRef.current = setTimeout(() => {
          try {
            editor.updateInstanceState({ isChangingStyle: false });
          } catch {
            // editor may be disposed during timeout
          }
          timeoutRef.current = null;
        }, HIDE_SELECTION_DURATION_MS);
      } catch {
        // editor may be disposed during unmount
      }
    };

    const unregister = editor.sideEffects.registerAfterChangeHandler("shape", handleShapeChange);
    return () => {
      unregister();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [editor]);
}

/** Renders nothing; subscribes to editor changes to hide selection for 0.5s on any change (except resize). */
export function HideSelectionOnChange() {
  useHideSelectionOnChange();
  return null;
}
